'use client';

import React, { useState } from 'react';
import Image from 'next/image';

interface AppImageProps {
    src: string;
    alt: string;
    width?: number;
    height?: number;
    className?: string;
    priority?: boolean;
    quality?: number;
    placeholder?: 'blur' | 'empty';
    blurDataURL?: string;
    fill?: boolean;
    sizes?: string;
    onClick?: () => void;
    fallbackSrc?: string;
    [key: string]: any;
}

function AppImage({
    src,
    alt,
    width,
    height,
    className = '',
    priority = false,
    quality = 75,
    placeholder = 'empty',
    blurDataURL,
    fill = false,
    sizes,
    onClick,
    fallbackSrc = '/assets/images/no_image.png',
    ...props
}: AppImageProps) {
    const [imageSrc, setImageSrc] = useState(src);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const isExternal = imageSrc.startsWith('http://') || imageSrc.startsWith('https://');

    const handleError = () => {
        if (!hasError && imageSrc !== fallbackSrc) {
            setImageSrc(fallbackSrc);
            setHasError(true);
        }
        setIsLoading(false);
    };

    const handleLoad = () => {
        setIsLoading(false);
        setHasError(false);
    };

    const commonClassName = `${className} ${isLoading ? 'bg-gray-200' : ''} ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`;

    const imageProps = {
        src: imageSrc,
        alt,
        className: commonClassName,
        priority,
        quality,
        onError: handleError,
        onLoad: handleLoad,
        onClick,
        unoptimized: isExternal,
        ...props,
    };

    if (fill) {
        return (
            <div data-component-id="src/components/ui/AppImage.tsx:75:12" data-component-path="src/components/ui/AppImage.tsx" data-component-line="75" data-component-end-line="82" data-component-file="AppImage.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`relative ${className}`}>
                <Image data-component-id="src/components/ui/AppImage.tsx:76:16" data-component-path="src/components/ui/AppImage.tsx" data-component-line="76" data-component-end-line="81" data-component-file="AppImage.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                    {...imageProps}
                    fill
                    sizes={sizes || '100vw'}
                    style={{ objectFit: 'cover' }}
                />
            </div>
        );
    }

    return (
        <Image data-component-id="src/components/ui/AppImage.tsx:87:8" data-component-path="src/components/ui/AppImage.tsx" data-component-line="87" data-component-end-line="91" data-component-file="AppImage.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
            {...imageProps}
            width={width || 400}
            height={height || 300}
        />
    );
}

export default AppImage;